import { set, merge } from 'lodash';
import axios from 'axios';
import moment from 'moment';
import ErcApiClient from '../../shared/services/erc-api-client';
import { mapAxiosResponse } from '../../shared/utils/api-utils';
import {
  ConfigUpdateItem,
  CreateJourneyConfigResponse,
  GetActiveConfigResponse,
  GetConfigsResponse,
  ImagePresignedResponse,
  JourneyConfig,
} from '../../shared/models/journey';
import DefaultConfig from '../assets/journeyconfig.json';

const FORMAT_MAP = {
  'image/jpeg': 'jpeg',
  'image/png': 'png',
};

export default class JourneyService {

  public static async uploadImage(orgID: number, image: File, key: string): Promise<string> {
    const response = await ErcApiClient.get(`/builder/org/${ orgID }/imageuploadtoken`, {
      params: {
        key,
        format: FORMAT_MAP[image.type],
      }
    });

    const { url: presignedURL } = mapAxiosResponse<ImagePresignedResponse>(response);

    await axios.put(presignedURL, image, {
      headers: {
        'Content-Type': image.type,
      },
    });

    const imageUrl = new URL(presignedURL);

    return `${ imageUrl.protocol }/${ imageUrl.pathname }`;
  }

  public static async getActiveConfig(orgID: number) : Promise<GetActiveConfigResponse | Error> {
    const listResponse = await ErcApiClient.get(`/builder/org/${ orgID }/configs/list`);

    const output = mapAxiosResponse<GetConfigsResponse>(listResponse);

    const configs = output.journeyConfigs
      .filter(c => moment(c.name).isValid())
      .sort((a, b) => moment(b.name).valueOf() - moment(a.name).valueOf());

    const active = configs.find(config => config.isPublished);

    if (active == null) {
      return {
        id: null,
        config: null,
        previous: null,
      };
    }

    let previous = null;
    if (configs.length > 1) {
      // eslint-disable-next-line prefer-destructuring
      previous = configs[1];

      const previousExportResponse = await this.exportConfig(orgID, previous.id);

      previous.config = previousExportResponse.data.journeyConfig;
    }

    const exportResponse = await this.exportConfig(orgID, active.id);

    return {
      id: active.id,
      config: exportResponse.data.journeyConfig,
      previous,
    };
  }

  public static async createConfig(orgID: number, config: JourneyConfig) : Promise<string | Error> {
    // Journey builder requires that a default language is set before we can create configs
    try {
      await ErcApiClient.post(`/builder/org/${ orgID }/settings`, {
        defaultLanguage: "en"
      });
    } catch (error) {
      // do nothing
    }

    const createResponse = await ErcApiClient.post(`/builder/org/${ orgID }/configs`, {
      name: moment().format(),
      journeyConfig: config,
    });

    const { id: configID } = mapAxiosResponse<CreateJourneyConfigResponse>(createResponse);

    await this.publishConfig(orgID, configID);

    return configID;
  }

  public static async updateConfig(orgID: number, configID: string, config: JourneyConfig, updates: ConfigUpdateItem[]) {
    const syncConfig = merge({}, DefaultConfig, config);
    if (updates) {
      updates.map(update => set(syncConfig, update.path, update.value));
    }

    await this.createConfig(orgID, syncConfig);
  }

  public static async publishConfig(orgID: number, configID: string) {
    return ErcApiClient.post(`/builder/org/${ orgID }/configs/${ configID }/publish`);
  }

  public static async exportConfig(orgID: number, configID: string) {
    return ErcApiClient.get(`/builder/org/${ orgID }/configs/${ configID }/export`);
  }
}
